import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import {
  type ExportSystem,
  type LatestExports,
  type ReexportResponse,
  type ReexportTarget,
  type RouteExport,
  latestExportsByRoute,
} from "@/lib/routeExports"
import { supabase } from "@/lib/supabase"

const KEY = ["collections-route-exports"]

export function routeExportsQueryKey(routeInstanceIds: string[]) {
  return [...KEY, [...routeInstanceIds].sort().join(",")]
}

/**
 * Export history for a set of routes, reduced to the most recent attempt per
 * route per system.
 *
 * Read straight from PostgREST rather than through the collections edge
 * function, for the same reason the access list is: the table is admin-only and
 * that depends on the caller's own JWT, so the check belongs in an RLS policy
 * the database enforces. A non-admin gets an empty result regardless of what
 * the UI renders.
 */
export function useRouteExports(routeInstanceIds: string[]) {
  return useQuery({
    queryKey: routeExportsQueryKey(routeInstanceIds),
    enabled: routeInstanceIds.length > 0,
    queryFn: async (): Promise<Record<string, LatestExports>> => {
      const { data, error } = await supabase
        .from("collections_route_exports")
        .select(
          "id, route_instance_id, system, trigger_source, status, external_route_id, error, attempted_by, attempted_at"
        )
        .in("route_instance_id", routeInstanceIds)
        .order("attempted_at", { ascending: false })

      if (error) throw new Error(error.message)

      return latestExportsByRoute((data ?? []) as RouteExport[])
    },
  })
}

/**
 * functions.invoke reports every non-2xx as "Edge Function returned a non-2xx
 * status code", which hides the reason the caller actually needs -- "Admin
 * access required", or which validation rule the request broke. The original
 * Response hangs off `context`, so read the real message out of its body.
 */
async function describeInvokeError(error: Error): Promise<string> {
  const context = (error as Error & { context?: unknown }).context
  if (context instanceof Response) {
    try {
      const body = (await context.clone().json()) as { message?: unknown }
      if (typeof body.message === "string" && body.message) return body.message
    } catch {
      // Non-JSON error body; fall back to the generic message.
    }
  }
  return error.message
}

/**
 * Push the given route/system pairs again.
 *
 * Takes pairs rather than routes-plus-systems so a partial repair can push each
 * route only to the system it is actually absent from, instead of touching
 * healthy routes to fix broken ones.
 *
 * Partial failure is the normal case rather than an exception -- one route can
 * fail Samsara's two-stop rule while every other push succeeds. The function
 * returns 207 for that, which counts as ok, so it arrives here as a result to
 * report rather than an error to throw.
 */
export function useReexportRoutes() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (targets: ReexportTarget[]): Promise<ReexportResponse> => {
      const { data, error } = await supabase.functions.invoke("collections-admin-reexport", {
        body: { targets },
      })

      if (error) throw new Error(await describeInvokeError(error as Error))

      return data as ReexportResponse
    },
    // A push can take a while -- up to 120 external calls, four at a time -- so
    // show a spinner immediately and let the outcome replace it, rather than
    // leaving the operator with nothing until it lands. Reusing the id is what
    // swaps the toast in place instead of stacking a second one under it.
    onMutate: (targets) => {
      const toastId = toast.loading(
        `Exporting ${targets.length} route ${targets.length === 1 ? "push" : "pushes"}…`
      )
      return { toastId }
    },
    onSuccess: (result, _targets, context) => {
      if (result.failed === 0) {
        toast.success(result.message, { id: context?.toastId })
      } else {
        toast.warning(result.message, {
          id: context?.toastId,
          // Long-lived: a partial failure is the case worth reading, and the
          // default few seconds is not enough to read several lines.
          duration: 30000,
          description: result.details
            .filter((detail) => detail.status === "error")
            .map((detail) => `${detail.routeCode} (${detail.system}): ${detail.message}`)
            .join("\n"),
        })
      }
      void queryClient.invalidateQueries({ queryKey: KEY })
      void queryClient.invalidateQueries({ queryKey: ["collections", "route_instances"] })
    },
    onError: (e: Error, _targets, context) => toast.error(e.message, { id: context?.toastId }),
  })
}

export type { ExportSystem, LatestExports, ReexportTarget, RouteExport }
